/**
 * The cast (docs/proposals/ai-personas-voice-and-sound.md §The cast, revision 2026-10-16): twelve
 * original characters who are people — at least one Black, Hispanic, Arab, Asian and Caucasian
 * character, at least six female and six male. `heritage` and `gender` are art-direction metadata
 * only: never shown to a child, never used for play, voice selection or strength.
 *
 * A persona is game-independent presentation: each game binds characters to its seats or levels.
 * `style` is how the persona PLAYS (P5 applies it inside the strength budget); `warmth` (0–1) is
 * how the persona TALKS and drives the Bridge partner rule (the warmest persona partners the child).
 * Ages are mixed on purpose; portraits come from scripts/assets/personas.images.json and ship only
 * after a human reviews them for representation and kindness.
 */

export type Heritage = 'black' | 'hispanic' | 'arab' | 'asian-east' | 'asian-south' | 'caucasian';
export type Gender = 'female' | 'male';
export type AgeBand = 'kid' | 'teen' | 'adult' | 'elder';
export type PlayingStyle = 'chaotic' | 'eager' | 'cautious' | 'tricky' | 'solid';

export interface Persona {
  readonly id: string;
  readonly name: string;
  readonly heritage: Heritage;
  readonly gender: Gender;
  readonly age: AgeBand;
  /** Art-direction portrait, served from /personas/. Ships only after human review. */
  readonly portrait: string;
  /** How the persona plays (P5 applies it inside the strength budget). */
  readonly style: PlayingStyle;
  /** 0–1; the warmest persona at a table partners the child in Bridge. */
  readonly warmth: number;
  /** One line per locale for the picker card. */
  readonly blurb: Readonly<Record<'en' | 'fr' | 'es', string>>;
}

export const CAST: readonly Persona[] = [
  {
    id: 'amara', name: 'Amara', heritage: 'black', gender: 'female', age: 'kid',
    portrait: '/personas/amara.webp', style: 'eager', warmth: 0.9,
    blurb: {
      en: 'Warm and cheerful — she cheers for every good move you make.',
      fr: 'Chaleureuse et joyeuse — elle applaudit chacun de tes bons coups.',
      es: 'Cálida y alegre: aplaude cada buena jugada que haces.',
    },
  },
  {
    id: 'mateo', name: 'Mateo', heritage: 'hispanic', gender: 'male', age: 'kid',
    portrait: '/personas/mateo.webp', style: 'chaotic', warmth: 0.7,
    blurb: {
      en: 'Playful and a little unpredictable — who knows what he tries next!',
      fr: 'Joueur et un peu imprévisible — qui sait ce qu’il va tenter !',
      es: 'Juguetón y un poco impredecible: ¡quién sabe qué probará ahora!',
    },
  },
  {
    id: 'layla', name: 'Layla', heritage: 'arab', gender: 'female', age: 'kid',
    portrait: '/personas/layla.webp', style: 'eager', warmth: 0.8,
    blurb: {
      en: 'Curious and kind — she loves discovering new ideas on the board.',
      fr: 'Curieuse et gentille — elle adore découvrir de nouvelles idées sur le plateau.',
      es: 'Curiosa y amable: le encanta descubrir ideas nuevas en el tablero.',
    },
  },
  {
    id: 'kenji', name: 'Kenji', heritage: 'asian-east', gender: 'male', age: 'teen',
    portrait: '/personas/kenji.webp', style: 'tricky', warmth: 0.5,
    blurb: {
      en: 'Thoughtful and sharp — watch out for his clever little traps.',
      fr: 'Réfléchi et vif — méfie-toi de ses petits pièges malins.',
      es: 'Reflexivo y agudo: cuidado con sus pequeñas trampas.',
    },
  },
  {
    id: 'priya', name: 'Priya', heritage: 'asian-south', gender: 'female', age: 'kid',
    portrait: '/personas/priya.webp', style: 'cautious', warmth: 0.75,
    blurb: {
      en: 'Careful and steady — she keeps her pieces safe and sound.',
      fr: 'Prudente et régulière — elle garde ses pièces bien en sécurité.',
      es: 'Cuidadosa y constante: mantiene sus piezas a salvo.',
    },
  },
  {
    id: 'oliver', name: 'Oliver', heritage: 'caucasian', gender: 'male', age: 'kid',
    portrait: '/personas/oliver.webp', style: 'chaotic', warmth: 0.8,
    blurb: {
      en: 'Eager and fun — full of plans, and half of them even work!',
      fr: 'Enthousiaste et amusant — plein de plans, et la moitié marchent !',
      es: 'Entusiasta y divertido: lleno de planes, ¡y la mitad funciona!',
    },
  },
  {
    id: 'marisol', name: 'Coach Marisol', heritage: 'hispanic', gender: 'female', age: 'adult',
    portrait: '/personas/marisol.webp', style: 'solid', warmth: 0.85,
    blurb: {
      en: 'Calm and encouraging — she plays strong and never gloats.',
      fr: 'Calme et encourageante — elle joue fort et ne se vante jamais.',
      es: 'Tranquila y alentadora: juega fuerte y nunca se jacta.',
    },
  },
  {
    id: 'kofi', name: 'Grandpa Kofi', heritage: 'black', gender: 'male', age: 'elder',
    portrait: '/personas/kofi.webp', style: 'solid', warmth: 0.95,
    blurb: {
      en: 'Patient and wise — he has played a thousand games and still loves them.',
      fr: 'Patient et sage — il a joué mille parties et les aime encore.',
      es: 'Paciente y sabio: ha jugado mil partidas y todavía las disfruta.',
    },
  },
  {
    id: 'karim', name: 'Karim', heritage: 'arab', gender: 'male', age: 'teen',
    portrait: '/personas/karim.webp', style: 'tricky', warmth: 0.6,
    blurb: {
      en: 'Quiet and confident — his surprise moves come out of nowhere.',
      fr: 'Discret et sûr de lui — ses coups surprises sortent de nulle part.',
      es: 'Sereno y seguro: sus jugadas sorpresa salen de la nada.',
    },
  },
  {
    id: 'hannah', name: 'Hannah', heritage: 'caucasian', gender: 'female', age: 'kid',
    portrait: '/personas/hannah.webp', style: 'cautious', warmth: 0.7,
    blurb: {
      en: 'Bright and curious — she thinks first, then strikes.',
      fr: 'Vive et curieuse — elle réfléchit d’abord, puis frappe.',
      es: 'Brillante y curiosa: primero piensa, luego actúa.',
    },
  },
  {
    id: 'wei', name: 'Uncle Wei', heritage: 'asian-east', gender: 'male', age: 'adult',
    portrait: '/personas/wei.webp', style: 'solid', warmth: 0.8,
    blurb: {
      en: 'Friendly and easy-going — a fair fight, every single time.',
      fr: 'Sympa et détendu — un combat loyal, à chaque partie.',
      es: 'Amable y relajado: una pelea justa, siempre.',
    },
  },
  {
    id: 'fatima', name: 'Grandma Fatima', heritage: 'arab', gender: 'female', age: 'elder',
    portrait: '/personas/fatima.webp', style: 'eager', warmth: 1.0,
    blurb: {
      en: 'Warm as toast — she plays for the fun of it, and to win, a little.',
      fr: 'Chaleureuse comme un feu de bois — elle joue pour le plaisir, et un peu pour gagner.',
      es: 'Acalorada como pan recién hecho: juega por diversión… y un poco para ganar.',
    },
  },
];

/** The build fails if the roster loses any of these heritages (spec §cast revision, rule 2). */
export const REQUIRED_HERITAGES: readonly Heritage[] = ['black', 'hispanic', 'arab', 'asian-east', 'asian-south', 'caucasian'];
/** At least six female and six male characters (spec §cast revision, rule 3). */
export const MIN_PER_GENDER = 6;

export const personaById = (id: string): Persona | undefined => CAST.find((p) => p.id === id);
